import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '';

// ============================================================================
// Type Definitions - Match backend Zod schemas
// ============================================================================

// Order Item type (used in Order create/update)
export interface OrderItem {
  name: string;
  quantity: number;
  price: number;
  is_flagged?: boolean;
  issue_type?: 'missing' | 'wrong' | 'damaged' | 'quality';
}

// Create Order payload
export interface CreateOrderPayload {
  store_id: string;
  aggregator_order_id: string;
  order_date: string; // ISO datetime
  total_amount: number;
  items: OrderItem[];
  customer_id_hash?: string;
  delivery_driver_id?: string;
  refund_amount?: number;
  refund_reason?: string;
  refund_items?: OrderItem[];
  refund_date?: string; // ISO datetime
}

// Update Order payload
export interface UpdateOrderPayload {
  status?: 'pending_review' | 'classified' | 'disputed' | 'resolved' | 'skipped';
  refund_amount?: number;
  refund_reason?: string;
  refund_items?: OrderItem[];
  refund_date?: string; // ISO datetime
}

// Update Dispute payload
export interface UpdateDisputePayload {
  draft_text?: string;
  final_text?: string;
  status?: 'draft' | 'pending_review' | 'approved' | 'submitted' | 'won' | 'lost' | 'expired';
  outcome?: 'won' | 'lost' | 'partial' | 'expired';
  recovered_amount?: number;
  submission_method?: string;
  aggregator_response?: string;
}

// Store thresholds config
export interface StoreThresholds {
  bucket1_item_rate?: number;
  bucket1_min_items?: number;
  bucket2_value_rate?: number;
  bucket2_min_order?: number;
  bucket3_min_order?: number;
  bucket4_claim_count?: number;
  bucket4_window_days?: number;
}

// Store config
export interface StoreConfig {
  thresholds?: StoreThresholds;
  restaurant_type?: string;
  dispute_window_days?: number;
}

// Create Store payload
export interface CreateStorePayload {
  name: string;
  external_id?: string;
  aggregator: 'ubereats' | 'doordash' | 'grubhub';
  config?: StoreConfig;
}

// Update Store payload
export interface UpdateStorePayload {
  name?: string;
  external_id?: string;
  config?: StoreConfig;
  is_active?: boolean;
}

// Query params type
export type QueryParams = Record<string, string | number | boolean | undefined>;

// ============================================================================
// API Client
// ============================================================================

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token from localStorage
api.interceptors.request.use(
  (config) => {
    // Read token from persisted auth store
    try {
      const authData = localStorage.getItem("recoverrefunds-auth");
      if (authData) {
        const { state } = JSON.parse(authData);
        if (state?.token) {
          // Use set method for axios 1.x compatibility
          config.headers.set('Authorization', `Bearer ${state.token}`);
        }
      }
    } catch {
      // Ignore parse errors
    }
    return config;
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not a retry, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Dynamic import to avoid circular dependency
        const { useAuthStore } = await import('../stores/authStore');
        await useAuthStore.getState().refreshAuth();

        // Retry original request
        return api(originalRequest);
      } catch {
        // Refresh failed, logout
        const { useAuthStore } = await import('../stores/authStore');
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// ============================================================================
// API Endpoints
// ============================================================================

export const authApi = {
  login: (email: string, password: string) =>
    api.post('/api/auth/login', { email, password }),
  register: (data: { email: string; password: string; invite_code: string; company_name: string }) =>
    api.post('/api/auth/register', data),
  me: () => api.get('/api/auth/me'),
  logout: () => api.post('/api/auth/logout'),
};

export const ordersApi = {
  list: (params?: QueryParams) => api.get('/api/orders', { params }),
  get: (id: string) => api.get(`/api/orders/${id}`),
  create: (data: CreateOrderPayload) => api.post('/api/orders', data),
  update: (id: string, data: UpdateOrderPayload) => api.patch(`/api/orders/${id}`, data),
  import: (storeId: string, orders: Omit<CreateOrderPayload, 'store_id'>[]) =>
    api.post('/api/orders/import', { store_id: storeId, orders }),
};

export const disputesApi = {
  list: (params?: QueryParams) => api.get('/api/disputes', { params }),
  get: (id: string) => api.get(`/api/disputes/${id}`),
  create: (orderId: string) => api.post('/api/disputes', { order_id: orderId }),
  update: (id: string, data: UpdateDisputePayload) => api.patch(`/api/disputes/${id}`, data),
  approve: (id: string) => api.post(`/api/disputes/${id}/approve`),
  outcome: (id: string, data: { outcome: 'won' | 'lost' | 'partial' | 'expired'; recovered_amount?: number; aggregator_response?: string }) =>
    api.post(`/api/disputes/${id}/outcome`, data),
  // Email-based submission (Grubhub only)
  emailSubmit: (id: string, data: { restaurant_email: string; grubhub_account_id?: string; dispute_text: string }) =>
    api.post(`/api/disputes/${id}/email-submit`, data),
  emailPreview: (id: string, params: { restaurant_email: string; grubhub_account_id?: string }) =>
    api.get(`/api/disputes/${id}/email-preview`, { params }),
};

export const storesApi = {
  list: (params?: QueryParams) => api.get('/api/stores', { params }),
  get: (id: string) => api.get(`/api/stores/${id}`),
  create: (data: CreateStorePayload) => api.post('/api/stores', data),
  update: (id: string, data: UpdateStorePayload) => api.patch(`/api/stores/${id}`, data),
};

export const analyticsApi = {
  summary: (params?: QueryParams) => api.get('/api/analytics/summary', { params }),
  trend: (params?: QueryParams) => api.get('/api/analytics/trend', { params }),
  buckets: (params?: QueryParams) => api.get('/api/analytics/buckets', { params }),
  topItems: (params?: QueryParams) => api.get('/api/analytics/top-items', { params }),
  stores: (params?: QueryParams) => api.get('/api/analytics/stores', { params }),
};

export const waitlistApi = {
  join: (data: { email: string; company_name?: string; store_count?: number; aggregators?: string[] }) =>
    api.post('/api/waitlist', data),
  status: (email: string) => api.get(`/api/waitlist/status/${email}`),
};

export const exportApi = {
  disputes: () => api.get('/api/export/disputes', { responseType: 'blob' }),
  orders: () => api.get('/api/export/orders', { responseType: 'blob' }),
  summary: () => api.get('/api/export/summary', { responseType: 'blob' }),
  report: (format: 'xlsx' | 'csv' = 'xlsx') =>
    api.get('/api/export/report', { params: { format }, responseType: 'blob' }),
};

export interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  link?: string;
  is_read: boolean;
  created_at: string;
}

export const notificationsApi = {
  list: (params?: { limit?: number; offset?: number; unread_only?: boolean }) =>
    api.get<{ notifications: Notification[]; unread_count: number; has_more: boolean }>(
      '/api/notifications',
      { params }
    ),
  markRead: (id: string) => api.patch(`/api/notifications/${id}/read`),
  markAllRead: () => api.post('/api/notifications/mark-all-read'),
  unreadCount: () => api.get<{ count: number }>('/api/notifications/unread-count'),
};

export interface Evidence {
  id: string;
  type: 'receipt_photo' | 'seal_photo' | 'cctv_link' | 'other';
  file_name?: string;
  file_size?: number;
  mime_type?: string;
  external_url?: string;
  created_at: string;
}

export const evidenceApi = {
  list: (disputeId: string) =>
    api.get<{ evidence: Evidence[] }>(`/api/disputes/${disputeId}/evidence`),
  upload: (disputeId: string, file: File, type: string = 'other') => {
    const formData = new FormData();
    formData.append(type, file);
    return api.post<{ id: string; message: string; file_name: string; file_size: number }>(
      `/api/disputes/${disputeId}/evidence`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
  addLink: (disputeId: string, url: string, type: string = 'cctv_link') =>
    api.post<{ id: string; message: string }>(`/api/disputes/${disputeId}/evidence/link`, { url, type }),
  delete: (disputeId: string, evidenceId: string) =>
    api.delete(`/api/disputes/${disputeId}/evidence/${evidenceId}`),
};

// ML Prediction types
export interface MLPrediction {
  win_probability: number;
  confidence: number;
  recommended_action: 'auto_dispute' | 'review' | 'skip';
  contributing_factors: {
    feature: string;
    impact: 'positive' | 'negative';
    weight: number;
  }[];
  ml_score: number;
  training_samples: number;
  min_samples_required: number;
  is_trained: boolean;
}

export interface MLStats {
  tenant_id: string;
  training_samples: number;
  last_trained: string | null;
  accuracy: number;
  weights: Record<string, number>;
  model_version: string;
  algorithm: string;
  features_used: number;
}

// ============================================================================
// Integrations API (POS & Platform Verification)
// ============================================================================

export interface POSSystem {
  type: string;
  name: string;
  auth_type: 'oauth2' | 'api_key';
  webhook_supported: boolean;
}

export interface POSIntegration {
  id: string;
  store_id: string;
  pos_type: string;
  pos_name: string;
  merchant_id: string | null;
  location_id: string | null;
  status: 'pending' | 'connected' | 'error' | 'disconnected';
  last_sync_at: string | null;
  sync_error: string | null;
  created_at: string;
}

export interface PlatformVerification {
  id: string;
  store_id: string;
  aggregator: string;
  verification_type: 'oauth' | 'pos' | 'email' | 'manual';
  verification_status: 'pending' | 'email_sent' | 'verified' | 'failed' | 'expired';
  verified_at: string | null;
  expires_at: string | null;
}

export interface ConnectPOSPayload {
  store_id: string;
  pos_type: 'toast' | 'square' | 'clover';
  credentials: {
    merchant_id?: string;
    location_id?: string;
    api_key?: string;
    api_secret?: string;
    access_token?: string;
    refresh_token?: string;
  };
}

export const integrationsApi = {
  // POS Systems
  availablePOS: () =>
    api.get<{ data: POSSystem[] }>('/api/integrations/pos/available'),
  listPOS: () =>
    api.get<{ data: POSIntegration[] }>('/api/integrations/pos'),
  connectPOS: (data: ConnectPOSPayload) =>
    api.post<{ id: string; message: string }>('/api/integrations/pos/connect', data),
  disconnectPOS: (id: string) =>
    api.delete(`/api/integrations/pos/${id}`),
  testPOS: (id: string) =>
    api.post<{ success: boolean; message: string }>(`/api/integrations/pos/${id}/test`),
  syncPOS: (id: string, params?: { start_date?: string; end_date?: string }) =>
    api.post<{ success: boolean; orders_fetched: number }>(`/api/integrations/pos/${id}/sync`, null, { params }),

  // Platform Verification
  listVerifications: () =>
    api.get<{ data: PlatformVerification[] }>('/api/integrations/verification'),
  startOAuth: (store_id: string, aggregator: 'ubereats' | 'doordash' | 'grubhub') =>
    api.post<{ redirect_url: string; verification_id: string }>('/api/integrations/verification/oauth/start', { store_id, aggregator }),
  startEmailVerification: (store_id: string, aggregator: string, restaurant_email: string, restaurant_name: string) =>
    api.post<{ status: string; message: string; verification_id: string }>('/api/integrations/verification/email/start', {
      store_id, aggregator, restaurant_email, restaurant_name
    }),
  startPOSVerification: (store_id: string, aggregator: string, pos_type: string) =>
    api.post<{ status: string; message: string; verification_id: string }>('/api/integrations/verification/pos/start', {
      store_id, aggregator, pos_type
    }),
  getVerificationStatus: (storeId: string, aggregator: string) =>
    api.get<{ status: string; type: string | null; verifiedAt: string | null }>(`/api/integrations/verification/${storeId}/${aggregator}`),
};

// ============================================================================
// Billing API
// ============================================================================

export interface BillingSummary {
  plan: 'free' | 'pro' | 'enterprise';
  period_start: string;
  period_end: string;
  active_stores: number;
  max_stores: number;
  disputes_submitted: number;
  disputes_won: number;
  disputes_lost: number;
  total_recovered: number;
  success_fee_rate: number;
  success_fee_amount: number;
  win_rate: number;
}

export const billingApi = {
  summary: () => api.get<BillingSummary>('/api/billing/summary'),
  history: (months?: number) => api.get('/api/billing/history', { params: { months } }),
  breakdown: () => api.get('/api/billing/breakdown'),
  subscription: () => api.get('/api/billing/subscription'),
  checkout: (plan: 'pro' | 'enterprise') => api.post<{ url: string }>('/api/billing/checkout', { plan }),
  portal: () => api.get<{ url: string }>('/api/billing/portal'),
};

export const mlApi = {
  // Get ML prediction for a dispute
  getDisputePrediction: (disputeId: string) =>
    api.get<{
      dispute_id: string;
      order_id: string;
      prediction: MLPrediction;
      rules_score: number;
      bucket: string;
    }>(`/api/disputes/${disputeId}/ml-prediction`),

  // Get ML prediction for an order (before dispute creation)
  getOrderPrediction: (orderId: string) =>
    api.get<{
      order_id: string;
      prediction: MLPrediction;
      rules_bucket: string | null;
      rules_score: number | null;
    }>(`/api/disputes/orders/${orderId}/ml-prediction`),

  // Get ML model statistics
  getStats: () => api.get<MLStats>('/api/disputes/ml/stats'),

  // Trigger model training (admin only)
  train: (global?: boolean) =>
    api.post<{
      success: boolean;
      message: string;
      samples: number;
      accuracy: number;
      weights: Record<string, number>;
    }>(`/api/disputes/ml/train${global ? '?global=true' : ''}`),

  // Get enhanced recommendation with ML
  getEnhancedRecommendation: (disputeId: string) =>
    api.get<{
      dispute_id: string;
      primaryReason: string;
      confidence: number;
      supportingEvidence: string[];
      suggestedText: string;
      historicalWinRate: number;
      ml_prediction: MLPrediction;
    }>(`/api/disputes/${disputeId}/enhanced-recommendation`),
};
